import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Input, Popconfirm, Segmented, Space, Table, Tag, Typography, message } from 'antd';
import {
  AppstoreOutlined, BarsOutlined, DatabaseOutlined, DeleteOutlined, DownloadOutlined, ExportOutlined, EyeOutlined,
  FileImageOutlined, FundProjectionScreenOutlined, PictureOutlined, SearchOutlined, UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { fetchMaterialList, type MaterialItem } from '../api/material';
import { deleteDashboardAsset, listDashboardAssets, type DashboardAsset } from '../api/assets';
import { fetchScenes } from '../api/scenes';
import { fetchPages } from '../api/pages';
import { fetchDtbaseCatalog, type DtbaseCatalogItem } from '../api/pay';
import { usePayIntentStore } from '../components/payment/payIntent';

// ============================================================
// E（2026-10-03）：我的资产（P1 聚合）
// 三类来源：市场素材（已购/已授权）+ 上传资产（dashboard-assets）+ 我的作品（3D 场景/大屏）
// P2 规划：导出入库（source/refId）、编辑器内资产选择器；P3 规划：免费额度/商用授权
// ============================================================

const SOURCE_LABEL: Record<string, string> = { market: '已购素材', upload: '上传资产', work: '我的作品' };
const KIND_LABEL: Record<string, string> = {
  image: '图片', model: '模型', template: '模板', font: '字体',
  dashboard: '大屏', scene: '3D 场景', component: '组件',
};

interface AssetCard {
  id: string;
  name: string;
  source: 'market' | 'upload' | 'work';
  kind: string;
  cover?: string;
  meta?: string;
  actions: React.ReactNode;
}

function fmtSize(bytes?: number): string {
  if (!bytes) return '-';
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${bytes}B`;
}

function fmtDate(v?: string): string {
  return v ? new Date(v).toLocaleDateString() : '-';
}

export default function MyAssets() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'all' | 'market' | 'upload' | 'work'>('all');
  const [kw, setKw] = useState('');
  // 2026-10-10（用户要求）：卡片/列表切换 + 分页管理（参考可视化大屏列表）
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => (localStorage.getItem('assetsView') as 'card' | 'list') || 'card');
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  const [uploads, setUploads] = useState<DashboardAsset[]>([]);
  const [scenes, setScenes] = useState<Array<{ id: string; name?: string; thumbnail?: string; updatedAt?: string }>>([]);
  const [pages, setPages] = useState<Array<{ id: string; name?: string; updatedAt?: string }>>([]);
  const [bases, setBases] = useState<DtbaseCatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const openPay = usePayIntentStore((s) => s.openPay);

  const load = async () => {
    setLoading(true);
    try {
      const [m, u, s, p] = await Promise.all([
        fetchMaterialList().catch(() => ({ list: [] as MaterialItem[] })),
        listDashboardAssets().catch(() => [] as DashboardAsset[]),
        fetchScenes().catch(() => []),
        fetchPages().catch(() => []),
      ]);
      setMaterials(m.list ?? []);
      setUploads(u ?? []);
      setScenes((s as typeof scenes) ?? []);
      setPages((p as typeof pages) ?? []);
      fetchDtbaseCatalog()
        .then((c) => setBases(c.items ?? []))
        .catch(() => setBases([]));
    } catch {
      message.error('资产加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeUpload = async (id: string) => {
    try {
      await deleteDashboardAsset(id);
      message.success('已删除');
      setUploads((prev) => prev.filter((a) => a.id !== id));
    } catch {
      message.error('删除失败');
    }
  };

  const cards = useMemo<AssetCard[]>(() => {
    const market: AssetCard[] = materials
      .filter((m) => !m.locked)
      .map((m) => ({
        id: `m-${m.id}`,
        name: m.name,
        source: 'market',
        kind: m.assetType || 'component',
        cover: m.coverImageUrl,
        meta: m.priceType === 'free' ? '免费已授权' : '已购/套餐授权',
        actions: (
          <Space size={4}>
            <Button size="small" type="primary" ghost icon={<DownloadOutlined />} disabled={!m.downloadUrl}
              onClick={() => m.downloadUrl && window.open(m.downloadUrl)}>下载</Button>
          </Space>
        ),
      }));
    const upload: AssetCard[] = uploads.map((a) => ({
      id: `u-${a.id}`,
      name: a.name,
      source: 'upload',
      kind: a.assetType || 'image',
      cover: a.assetType === 'image' ? a.url : undefined,
      meta: `${fmtSize(a.size)} · ${a.visibility === 'tenant' ? '租户共享' : '仅自己'} · ${fmtDate(a.createdAt)}`,
      actions: (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(a.url)}>预览</Button>
          <Popconfirm title="确认删除该资产？" onConfirm={() => void removeUpload(a.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    }));
    const work: AssetCard[] = [
      ...scenes.map((s) => ({
        id: `s-${s.id}`,
        name: s.name || `场景 ${s.id.slice(0, 8)}`,
        source: 'work' as const,
        kind: 'scene',
        cover: s.thumbnail,
        meta: `3D 场景 · 更新 ${fmtDate(s.updatedAt)}`,
        actions: (
          <Space size={4}>
            <Button size="small" type="primary" ghost icon={<FundProjectionScreenOutlined />}
              onClick={() => navigate(`/editor/scene/${s.id}`)}>打开</Button>
            <Button size="small" icon={<ExportOutlined />} onClick={() => navigate('/projects')}>去导出</Button>
          </Space>
        ),
      })),
      ...pages.map((p) => ({
        id: `p-${p.id}`,
        name: p.name || `大屏 ${p.id.slice(0, 8)}`,
        source: 'work' as const,
        kind: 'dashboard',
        meta: `大屏 · 更新 ${fmtDate(p.updatedAt)}`,
        actions: (
          <Space size={4}>
            <Button size="small" type="primary" ghost icon={<AppstoreOutlined />}
              onClick={() => navigate(`/editor/dashboard/${p.id}`)}>打开</Button>
            <Button size="small" icon={<ExportOutlined />} onClick={() => navigate('/dashboards')}>去导出</Button>
          </Space>
        ),
      })),
    ];
    return [...market, ...upload, ...work];
  }, [materials, uploads, scenes, pages, navigate]);

  const shown = (tab === 'all' ? cards : cards.filter((c) => c.source === tab)).filter((c) => {
    const k = kw.trim();
    if (!k) return true;
    return c.name.includes(k) || (KIND_LABEL[c.kind] || c.kind).includes(k) || SOURCE_LABEL[c.source].includes(k);
  });
  const count = (s: AssetCard['source']) => cards.filter((c) => c.source === s).length;

  const sourceIcon = (s: AssetCard['source']) =>
    s === 'market' ? <PictureOutlined /> : s === 'upload' ? <UploadOutlined /> : <FileImageOutlined />;

  return (
    <div style={{ padding: 16 }}>
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold m-0">我的资产</h2>
        <Space wrap>
          {/* 2026-10-04 用户要求：按"素材库"定位补充搜索 */}
          <Input
            allowClear
            placeholder="搜索资产名称 / 类型"
            value={kw}
            onChange={(e) => setKw(e.target.value)}
            prefix={<SearchOutlined style={{ color: '#999' }} />}
            style={{ width: 220 }}
            data-testid="assets-search"
          />
          <Segmented
            value={tab}
            onChange={(v) => setTab(v as typeof tab)}
            options={[
              { label: `全部 ${cards.length}`, value: 'all' },
              { label: `已购素材 ${count('market')}`, value: 'market' },
              { label: `上传资产 ${count('upload')}`, value: 'upload' },
              { label: `我的作品 ${count('work')}`, value: 'work' },
            ]}
          />
          <Segmented
            value={viewMode}
            onChange={(v) => { const m = v as 'card' | 'list'; setViewMode(m); localStorage.setItem('assetsView', m); }}
            options={[
              { value: 'card', icon: <AppstoreOutlined />, label: '卡片' },
              { value: 'list', icon: <BarsOutlined />, label: '列表' },
            ]}
            data-testid="assets-view-toggle"
          />
        </Space>
      </div>

      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        已购素材=购买/套餐授权的市场素材（在「素材库」购买）；上传资产=大屏资源库上传的文件；我的作品=3D 场景与大屏（导出入口在各自列表页）。
      </Typography.Paragraph>

      {/* B8：行业基座市场（单购试点；价格与开放状态由推广后台配置）
          2026-10-10（用户要求）：补基座快照图，卡片尺寸/结构与「素材库」卡片对齐（240px / 封面高 120） */}
      {bases.length > 0 && (
        <Card size="small" title="行业基座市场" style={{ marginBottom: 16 }} data-testid="dtbase-market">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {bases.map((b) => (
              <Card
                key={b.assetCode}
                size="small"
                hoverable
                styles={{ body: { padding: 10 } }}
                cover={(
                  <div
                    style={{ height: 120, background: '#1c1c2a', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                  >
                    {b.cover ? (
                      <img
                        alt={b.name}
                        src={b.cover}
                        loading="lazy"
                        data-testid={`dtbase-cover-${b.assetCode}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Space orientation="vertical" size={4} style={{ alignItems: 'center' }}>
                        <DatabaseOutlined style={{ fontSize: 26, color: '#4b5563' }} />
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>无快照</Typography.Text>
                      </Space>
                    )}
                  </div>
                )}
              >
                <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                  <Space size={6} style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Typography.Text strong ellipsis style={{ fontSize: 13, maxWidth: 150 }} title={b.name}>{b.name}</Typography.Text>
                    {b.purchasable ? <Tag color="gold" style={{ margin: 0 }}>¥{b.price}</Tag> : <Tag color="green" style={{ margin: 0 }}>免费</Tag>}
                  </Space>
                  <Typography.Text type="secondary" ellipsis style={{ fontSize: 11 }} title={String(b.description || '')}>
                    {String(b.description || '').slice(0, 60) || '—'}
                  </Typography.Text>
                  <Space size={4} wrap>
                    <Tag style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>{b.industry.join('/') || '通用'}</Tag>
                    <Tag style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>{b.models} 模型</Tag>
                    {b.priceTier ? <Tag color="blue" style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>{b.priceTier}</Tag> : null}
                    {b.access.includes('trial') ? <Tag color="cyan" style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>可试用</Tag> : null}
                  </Space>
                  <div>
                    {b.purchasable ? (
                      <Button
                        size="small"
                        type="primary"
                        data-testid={`dtbase-buy-${b.assetCode}`}
                        onClick={() =>
                          openPay(
                            { orderType: 'dtbase', productRef: b.assetCode, amount: b.price ?? undefined, subject: `基座购买：${b.name}` },
                            () => void load(),
                          )
                        }
                      >
                        购买
                      </Button>
                    ) : (
                      <Button size="small" disabled>
                        暂未开放单购
                      </Button>
                    )}
                  </div>
                </Space>
              </Card>
            ))}
          </div>
        </Card>
      )}

      {shown.length === 0 && !loading ? (
        <Empty description="暂无资产" />
      ) : viewMode === 'list' ? (
        <Table
          rowKey="id"
          size="middle"
          dataSource={shown}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          columns={[
            {
              title: '预览', width: 100,
              render: (_: unknown, c: AssetCard) => (c.cover ? (
                <img src={c.cover} alt={c.name} style={{ width: 72, height: 40, objectFit: 'cover', borderRadius: 4 }} />
              ) : (
                <div style={{ width: 72, height: 40, background: '#1c1c2a', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8a8f99', fontSize: 18 }}>
                  {sourceIcon(c.source)}
                </div>
              )),
            },
            { title: '名称', dataIndex: 'name', ellipsis: true, render: (v: string) => <Typography.Text strong>{v}</Typography.Text> },
            { title: '来源', dataIndex: 'source', width: 110, render: (v: AssetCard['source']) => <Tag>{SOURCE_LABEL[v]}</Tag> },
            { title: '类型', dataIndex: 'kind', width: 100, render: (v: string) => <Tag color="blue">{KIND_LABEL[v] || v}</Tag> },
            { title: '信息', dataIndex: 'meta', ellipsis: true, render: (v?: string) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{v || '—'}</Typography.Text> },
            { title: '操作', width: 200, render: (_: unknown, c: AssetCard) => c.actions },
          ]}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {shown.map((c) => (
            <Card
              key={c.id}
              size="small"
              loading={loading}
              cover={
                c.cover ? (
                  <img src={c.cover} alt={c.name} style={{ height: 120, objectFit: 'cover' }} />
                ) : (
                  <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: '#555', background: '#1c1c2a' }}>
                    {sourceIcon(c.source)}
                  </div>
                )
              }
            >
              <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                <Typography.Text ellipsis strong style={{ fontSize: 13 }} title={c.name}>{c.name}</Typography.Text>
                <Space size={4}>
                  <Tag>{SOURCE_LABEL[c.source]}</Tag>
                  <Tag color="blue">{KIND_LABEL[c.kind] || c.kind}</Tag>
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>{c.meta}</Typography.Text>
                <div>{c.actions}</div>
              </Space>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
